import React, { useState } from 'react';
import { useQualityEvents } from '../../context/QualityEventsContext';
import { useFilters } from '../../context/FilterContext';
import { useApp } from '../../context/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import TraceLabel from '../trace/TraceLabel';
import { useTrace } from '../../context/TraceContext';

const DeviationCreateDialog = ({ open, onOpenChange, onCreated }) => {
  const { createDeviation } = useQualityEvents();
  const { plant, area } = useFilters();
  const { currentRole } = useApp();
  const { pidTraceMode } = useTrace();

  const [formData, setFormData] = useState({
    title: '',
    detailedDescription: '',
    category: '',
    severity: '',
    eventDateTime: new Date().toISOString().slice(0, 16),
    immediateAction: '',
    batch: '',
    equipment: '',
    room: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const newDeviation = createDeviation({
        title: formData.title,
        detailedDescription: formData.detailedDescription,
        category: formData.category,
        severity: formData.severity,
        eventDateTime: formData.eventDateTime,
        immediateAction: formData.immediateAction,
        batch: formData.batch || null,
        equipment: formData.equipment || null,
        room: formData.room || null,
        plant,
        area: area !== 'all' ? area : null,
        reportedBy: currentRole?.name || 'System'
      });

      toast.success('Deviation created', {
        description: `Created ${newDeviation.id}`
      });

      onCreated?.(newDeviation);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        title: '',
        detailedDescription: '',
        category: '',
        severity: '',
        eventDateTime: new Date().toISOString().slice(0, 16),
        immediateAction: '',
        batch: '',
        equipment: '',
        room: ''
      });
    } catch (error) {
      toast.error('Failed to create deviation', {
        description: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[600px] max-h-[90vh] overflow-y-auto ${pidTraceMode ? 'border-cyan-300' : ''}`}>
        <TraceLabel prd="PRD §6.4" req="SM-DEV-001" />
        <DialogHeader>
          <DialogTitle>New Deviation</DialogTitle>
          <DialogDescription>
            Report a new deviation. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title / Short Description *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Brief description of the deviation"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="detailedDescription">Detailed Description *</Label>
            <Textarea
              id="detailedDescription"
              value={formData.detailedDescription}
              onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value })}
              placeholder="Provide detailed information about what happened"
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Documentation">Documentation</SelectItem>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Process">Process</SelectItem>
                  <SelectItem value="Environment">Environment</SelectItem>
                  <SelectItem value="Training">Training</SelectItem>
                  <SelectItem value="Supplier">Supplier</SelectItem>
                  <SelectItem value="Utilities">Utilities</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="severity">Severity *</Label>
              <Select
                value={formData.severity}
                onValueChange={(value) => setFormData({ ...formData, severity: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Minor">Minor</SelectItem>
                  <SelectItem value="Major">Major</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="eventDateTime">Event Date & Time *</Label>
            <Input
              id="eventDateTime"
              type="datetime-local"
              value={formData.eventDateTime}
              onChange={(e) => setFormData({ ...formData, eventDateTime: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="immediateAction">Immediate Action Taken</Label>
            <Textarea
              id="immediateAction"
              value={formData.immediateAction}
              onChange={(e) => setFormData({ ...formData, immediateAction: e.target.value })}
              placeholder="Describe any immediate actions taken"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="batch">Batch (optional)</Label>
              <Input
                id="batch"
                value={formData.batch}
                onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                placeholder="BATCH-XXX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment (optional)</Label>
              <Input
                id="equipment"
                value={formData.equipment}
                onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                placeholder="EQ-XXX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="room">Room (optional)</Label>
              <Input
                id="room"
                value={formData.room}
                onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                placeholder="RM-XXX"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#fd4816] hover:bg-[#ea380c] text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Deviation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DeviationCreateDialog;

